import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const getChartColors = () => {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    grid: isDark ? '#444' : '#e5e7eb',
    text: isDark ? '#fff' : '#111',
    bg1: isDark ? 'rgba(37,99,235,0.7)' : 'rgba(37,99,235,0.2)',
    bg2: isDark ? 'rgba(16,185,129,0.7)' : 'rgba(16,185,129,0.2)',
    bg3: isDark ? 'rgba(239,68,68,0.7)' : 'rgba(239,68,68,0.2)',
    bg4: isDark ? 'rgba(251,191,36,0.7)' : 'rgba(251,191,36,0.2)',
    bg5: isDark ? 'rgba(168,85,247,0.7)' : 'rgba(168,85,247,0.2)',
    bg6: isDark ? 'rgba(34,197,94,0.7)' : 'rgba(34,197,94,0.2)',
  };
};

const StatsSection = ({ api }) => {
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [rolesStats, setRolesStats] = useState(null);
  const [topScenarios, setTopScenarios] = useState(null);
  const [achievementsDist, setAchievementsDist] = useState(null);
  const [topUsers, setTopUsers] = useState(null);
  const [error, setError] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [collapseStats, setCollapseStats] = useState(false);
  const [collapseCharts, setCollapseCharts] = useState(false);
  
  const [drillModal, setDrillModal] = useState({ show: false, title: '', type: '', label: '' });
  const [drillData, setDrillData] = useState([]);
  const [loadingDrill, setLoadingDrill] = useState(false);
  const [showScenarioDetails, setShowScenarioDetails] = useState(false);

  useEffect(() => {
    loadStats();
    loadDailyStats();
    loadChartData();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.fetchStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Не удалось загрузить статистику');
    }
  };

  const loadDailyStats = async () => {
    setLoadingCharts(true);
    try {
      const data = await api.fetchDailyStats(dateFrom, dateTo);
      setDailyStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCharts(false);
    }
  };

  const loadChartData = async () => {
    const [roles, scenarios, achievements, users] = await Promise.all([
      api.fetchRolesStats(),
      api.fetchTopScenarios(),
      api.fetchAchievementsDist(),
      api.fetchTopUsers()
    ]);
    setRolesStats(roles);
    setTopScenarios(scenarios);
    setAchievementsDist(achievements);
    setTopUsers(users);
  };

  const handleDrillDown = async (type, label) => {
    setDrillModal({ show: true, title: `${type === 'role' ? 'Роль' : type === 'achievement' ? 'Достижение' : 'Пользователь'}: ${label}`, type, label });
    setLoadingDrill(true);
    try {
      if (type === 'role') {
        const users = await api.fetchUsersByRole(label);
        setDrillData(users);
      } else {
        setDrillData([]);
      }
    } catch (err) {
      setDrillData([]);
    } finally {
      setLoadingDrill(false);
    }
  };

  const statLabels = {
    total_dialogs: 'Всего диалогов', totalDialogs: 'Всего диалогов',
    total_scenarios: 'Всего сценариев', totalScenarios: 'Всего сценариев',
    total_achievements: 'Всего достижений',
    total_users: 'Всего пользователей', totalUsers: 'Всего пользователей',
    active_users: 'Активных пользователей', activeUsers: 'Активных пользователей',
    admins: 'Администраторов', managers: 'Модераторов',
    completedDialogs: 'Завершённых диалогов', completed_dialogs: 'Завершённых диалогов',
    'всего_пользователей': 'Всего пользователей', 'всего_диалогов': 'Всего диалогов',
    'завершённых_диалогов': 'Завершённых диалогов', 'всего_сценариев': 'Всего сценариев',
  };

  const colors = getChartColors();

  return (
    <div id="admin-stats" data-section="stats" className="w-full">
      <h2 className="text-3xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">Статистика</h2>
      
      {/* Mobile collapse buttons */}
      <div className="flex gap-2 mb-4 md:hidden">
        <button onClick={() => setCollapseStats(v => !v)} className="px-3 py-1 rounded bg-blue-100 dark:bg-gray-700 text-blue-900 dark:text-blue-200 font-semibold">{collapseStats ? 'Показать' : 'Скрыть'} метрики</button>
        <button onClick={() => setCollapseCharts(v => !v)} className="px-3 py-1 rounded bg-green-100 dark:bg-gray-700 text-green-900 dark:text-green-200 font-semibold">{collapseCharts ? 'Показать' : 'Скрыть'} графики</button>
      </div>

      {/* Stats Cards */}
      {!collapseStats && (
        error ? (
          <div className="text-center text-red-500 py-4">{error}</div>
        ) : stats && Object.keys(stats).length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6 mb-8">
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} className="p-4 rounded-xl text-center shadow bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
                <div className="text-gray-700 dark:text-gray-300">{statLabels[key] || key}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-400 py-8">Нет данных для отображения</div>
        )
      )}

      {/* Charts */}
      {!collapseCharts && (
        <>
          {/* Daily Stats Line Chart */}
          {dailyStats?.dates?.length > 0 && (
            <div className="rounded-2xl shadow-xl p-6 mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Динамика (30 дней)</h2>
              <div className="flex flex-col md:flex-row gap-2 mb-4 items-center">
                <label className="text-gray-700 dark:text-gray-300">Период:</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-100" />
                <span className="text-gray-500">—</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-100" />
                <button onClick={loadDailyStats} className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700">Показать</button>
              </div>
              {loadingCharts && <div className="text-center text-blue-500 py-4">Загрузка...</div>}
              <div className="min-w-[400px] min-h-[220px]">
                <Line
                  data={{
                    labels: dailyStats.dates,
                    datasets: [
                      { label: 'Новые пользователи', data: dailyStats.users_per_day, borderColor: colors.bg1, backgroundColor: colors.bg1, tension: 0.3 },
                      { label: 'Завершённые диалоги', data: dailyStats.dialogs_per_day, borderColor: colors.bg2, backgroundColor: colors.bg2, tension: 0.3 },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: { legend: { position: 'top', labels: { color: colors.text } } },
                    scales: {
                      x: { ticks: { color: colors.text }, grid: { color: colors.grid } },
                      y: { beginAtZero: true, ticks: { color: colors.text }, grid: { color: colors.grid } },
                    },
                  }}
                />
              </div>
            </div>
          )}

          {/* Roles Pie Chart */}
          {rolesStats && (
            <div className="rounded-2xl shadow-xl p-6 mb-6 flex flex-col items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Распределение ролей</h2>
              <div className="w-full max-w-xs">
                <Pie
                  data={{
                    labels: Object.keys(rolesStats),
                    datasets: [{ data: Object.values(rolesStats), backgroundColor: [colors.bg1, colors.bg2, colors.bg3, colors.bg4, colors.bg5] }],
                  }}
                  options={{
                    plugins: { legend: { position: 'bottom', labels: { color: colors.text } } },
                    onClick: (evt, elements) => {
                      if (elements.length > 0) {
                        const label = Object.keys(rolesStats)[elements[0].index];
                        handleDrillDown('role', label);
                      }
                    },
                  }}
                />
              </div>
            </div>
          )}

          {/* Top Scenarios Bar Chart */}
          {topScenarios && (
            <div className="rounded-2xl shadow-xl p-6 mb-6 flex flex-col md:flex-row items-center justify-between gap-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="w-full md:w-2/3">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Топ сценариев</h2>
                <div className="min-w-[400px] min-h-[220px]">
                  <Bar
                    data={{
                      labels: topScenarios.labels,
                      datasets: [{ label: 'Диалогов', data: topScenarios.counts, backgroundColor: colors.bg1, borderRadius: 8, maxBarThickness: 40 }],
                    }}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: { ticks: { color: colors.text }, grid: { color: colors.grid } },
                        y: { beginAtZero: true, ticks: { color: colors.text }, grid: { color: colors.grid } },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="w-full md:w-1/3">
                <h3 className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-200">Статистика</h3>
                <div className="space-y-2">
                  {topScenarios.labels.map((label, idx) => {
                    const count = topScenarios.counts[idx];
                    const max = Math.max(...topScenarios.counts);
                    const percent = max > 0 ? Math.round((count / max) * 100) : 0;
                    return (
                      <div key={label}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">{percent}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${percent}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => setShowScenarioDetails(true)} className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl">Подробнее</button>
              </div>
            </div>
          )}

          {/* Achievements Distribution */}
          {achievementsDist && (
            <div className="rounded-2xl shadow-xl p-6 mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Распределение достижений</h2>
              <div className="min-w-[400px] min-h-[220px]">
                <Bar
                  data={{
                    labels: achievementsDist.labels,
                    datasets: [{ label: 'Пользователей', data: achievementsDist.counts, backgroundColor: [colors.bg2, colors.bg3, colors.bg4, colors.bg5, colors.bg6] }],
                  }}
                  options={{
                    responsive: true,
                    indexAxis: 'y',
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { beginAtZero: true, ticks: { color: colors.text }, grid: { color: colors.grid } },
                      y: { ticks: { color: colors.text }, grid: { color: colors.grid } },
                    },
                  }}
                />
              </div>
            </div>
          )}

          {/* Top Users */}
          {topUsers && (
            <div className="rounded-2xl shadow-xl p-6 mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Топ-10 активных пользователей</h2>
              <div className="min-w-[400px] min-h-[220px]">
                <Bar
                  data={{
                    labels: topUsers.labels,
                    datasets: [{ label: 'Диалогов', data: topUsers.counts, backgroundColor: [colors.bg3, colors.bg4, colors.bg5, colors.bg6, colors.bg1, colors.bg2] }],
                  }}
                  options={{
                    responsive: true,
                    indexAxis: 'y',
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { beginAtZero: true, ticks: { color: colors.text }, grid: { color: colors.grid } },
                      y: { ticks: { color: colors.text }, grid: { color: colors.grid } },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Drill-down Modal */}
      {drillModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 w-full max-w-md mx-2 relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" onClick={() => setDrillModal({ show: false, title: '', type: '', label: '' })}>
              <XMarkIcon className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{drillModal.title}</h3>
            {loadingDrill ? (
              <div className="text-blue-500">Загрузка...</div>
            ) : drillData.length === 0 ? (
              <div className="text-gray-400">Нет данных</div>
            ) : (
              <ul className="list-disc pl-5 max-h-60 overflow-y-auto">
                {drillData.map(u => <li key={u.id}>{u.email} ({u.username})</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Scenario Details Modal */}
      {showScenarioDetails && topScenarios && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 w-full max-w-lg mx-2 relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" onClick={() => setShowScenarioDetails(false)}>
              <XMarkIcon className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Детали по сценариям</h3>
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="min-w-full bg-white dark:bg-gray-800 rounded-xl text-sm">
                <thead>
                  <tr className="text-left text-blue-900 dark:text-blue-200">
                    <th className="px-3 py-3">Сценарий</th>
                    <th className="px-3 py-3">Диалогов</th>
                    <th className="px-3 py-3">%</th>
                  </tr>
                </thead>
                <tbody>
                  {topScenarios.labels.map((label, idx) => {
                    const count = topScenarios.counts[idx];
                    const max = Math.max(...topScenarios.counts);
                    const percent = max > 0 ? Math.round((count / max) * 100) : 0;
                    return (
                      <tr key={label} className="border-b dark:border-gray-700">
                        <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100">{label}</td>
                        <td className="px-3 py-3 text-gray-700 dark:text-gray-300">{count}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <span>{percent}%</span>
                            <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${percent}%` }}></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsSection;
